import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n0, n2 } from "../../lib/format";
import { Notice, Stat } from "../../lib/ui";

/*
 * Receive invoice payment – port of receive_payment.php (API: /api/receivables, /api/receivables/pay)
 * Opened as /receive_payment?client_id=… or ?invoice_ids=1,2 (the invoices page links here); without either,
 * pick the client here. Pay any outstanding invoices in part or in full; optional SMS receipt to the client.
 */

export default function ReceivePayment() {
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [d, setD] = useState(null);
  const [amounts, setAmounts] = useState({});
  const [method, setMethod] = useState("Cash");
  const [sms, setSms] = useState(false);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const clientId = params.get("client_id"), ids = params.get("invoice_ids") || params.get("invoice_id");

  const load = useCallback(async () => {
    if (!clientId && !ids) { setD(null); return; }
    try {
      const r = await api.get("/api/receivables", { client_id: clientId || undefined, invoice_ids: ids || undefined });
      setD(r);
      const pre = new Set(r.preselect);
      setAmounts(Object.fromEntries(r.invoices.map((i) => [i.invoice_id, pre.size === 0 || pre.has(i.invoice_id) ? String(Math.round(i.remaining_due * 100) / 100) : ""])));
    } catch (e) { setNotice({ tone: "err", text: e.message }); }
  }, [api, clientId, ids]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/api/clients").then(setClients).catch(() => {}); }, [api]);

  const total = Object.values(amounts).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setNotice(null);
    try {
      const r = await api.post("/api/receivables/pay", { invoice_ids: d.invoices.map((i) => i.invoice_id), amounts, payment_method: method, notify_sms: sms });
      setNotice({ tone: "ok", text: r.message }); load();
    } catch (err) { setNotice({ tone: "err", text: err.message }); }
    finally { setBusy(false); }
  };

  return (
    <main className="pg">
      <PageHeader slug="receive_payment" />
      <Notice notice={notice} onClose={() => setNotice(null)} />
      <section className="panel" style={{ marginBottom: "1.25rem" }}>
        <div className="toolbar" style={{ borderBottom: 0 }}>
          <div className="field grow"><label htmlFor="rp-c">Client</label>
            <select id="rp-c" value={d?.client?.id || clientId || ""} onChange={(e) => setParams(e.target.value ? { client_id: e.target.value } : {})}>
              <option value="">Choose a client</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        </div>
      </section>
      {d && d.invoices.length === 0 && <div className="panel empty"><i className="fas fa-circle-check" style={{ color: "var(--up)" }} />This client has no outstanding invoices.</div>}
      {d && d.invoices.length > 0 && (
        <form onSubmit={submit}>
          <div className="ui-stats">
            <Stat icon="fa-user" tone="muted" label={d.client.phone || "No phone"} value={<span style={{ fontSize: "1rem" }}>{d.client.name}</span>} />
            <Stat icon="fa-file-invoice" tone="warn" label={`Outstanding · ${d.invoices.length} invoice${d.invoices.length === 1 ? "" : "s"}`} value={n0(d.grand_total)} unit="TZS" />
            <Stat icon="fa-hand-holding-dollar" tone="up" label="Paying now" value={n0(total)} unit="TZS" />
          </div>
          <section className="panel" style={{ marginBottom: "1rem" }}><div className="tbl-wrap"><table className="tbl">
            <thead><tr><th>Invoice</th><th>Date</th><th>Due</th><th className="num">Total</th><th className="num">Paid</th><th className="num">Remaining</th><th className="num">Pay now</th></tr></thead>
            <tbody>{d.invoices.map((i) => {
              const overdue = i.due_date && i.due_date < new Date().toISOString().slice(0, 10);
              return (
                <tr key={i.invoice_id}>
                  <td className="mono">#{i.invoice_id}</td><td>{i.invoice_date}</td><td style={overdue ? { color: "var(--danger)", fontWeight: 600 } : undefined}>{i.due_date}{overdue && " (overdue)"}</td>
                  <td className="num mono">{n2(i.total_amount)}</td><td className="num mono">{n2(i.paid_amount)}</td><td className="num mono">{n2(i.remaining_due)}</td>
                  <td className="num"><input className="cell" type="number" min="0" step="0.01" max={i.remaining_due} value={amounts[i.invoice_id] ?? ""} style={{ maxWidth: "9rem", marginLeft: "auto" }}
                    aria-label={`Amount for invoice ${i.invoice_id}`} onChange={(e) => setAmounts({ ...amounts, [i.invoice_id]: e.target.value })} /></td>
                </tr>
              );
            })}</tbody>
          </table></div></section>
          <section className="panel" style={{ padding: "1rem 1.25rem", display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "center" }}>
            <div className="seg" role="group" aria-label="Payment method" style={{ minWidth: "18rem" }}>
              {["Cash", "Bank", "Mobile"].map((m) => <button key={m} type="button" aria-pressed={method === m} onClick={() => setMethod(m)}>{m}</button>)}</div>
            <label style={{ display: "flex", gap: ".4rem", alignItems: "center", fontSize: ".875rem" }}>
              <input type="checkbox" checked={sms} onChange={(e) => setSms(e.target.checked)} disabled={!d.client.phone} /> Send the client an SMS receipt</label>
            <button type="submit" className="btn primary" disabled={busy || total <= 0} style={{ marginLeft: "auto" }}>
              <i className="fas fa-check" /> {busy ? "Processing…" : `Receive TZS ${n0(total)}`}</button>
          </section>
        </form>
      )}
    </main>
  );
}
